import { IconContains } from '@components/icons/contains';
import { IconContainsNot } from '@components/icons/contains-not';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconDirectionUp } from '@components/icons/direction-up';
import { IconEndsWith } from '@components/icons/ends-with';
import { IconEquals } from '@components/icons/equals';
import { IconEqualsLess } from '@components/icons/equals-less';
import { IconEqualsMore } from '@components/icons/equals-more';
import { IconEqualsNot } from '@components/icons/equals-not';
import { IconGreaterThan } from '@components/icons/greater-than';
import { IconGreaterThanOrEqual } from '@components/icons/greater-than-or-equal';
import { IconIsEmpty } from '@components/icons/is-empty';
import { IconIsNotEmpty } from '@components/icons/is-not-empty';
import { IconLessThan } from '@components/icons/less-than';
import { IconLessThanOrEqual } from '@components/icons/less-than-or-equal';
import { IconStartsWith } from '@components/icons/starts-with';
import { SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

type FieldTypeComparators =
  | 'file'
  | 'select'
  | 'multi_select'
  | 'boolean'
  | 'date'
  | 'number'
  | 'text';

const ComparatorRow = ({ Icon, label }: { Icon: React.ComponentType; label: string }) => {
  return (
    <Stack orientation="horizontal" space={3}>
      <Icon />
      {label}
    </Stack>
  );
};

export const useConditionComparators = () => {
  const { t } = useScopedTranslate(locale);

  const createComparator = (value: string, label: string, Icon: React.ComponentType) => ({
    value,
    label: <ComparatorRow Icon={Icon} label={label} />,
  });

  const getEmptyComparators = (
    value1 = 'empty',
    value2 = 'nempty',
    label1 = t('isEmpty'),
    label2 = t('isNotEmpty')
  ) => {
    return [
      createComparator(value1, label1, IconIsEmpty),
      createComparator(value2, label2, IconIsNotEmpty),
    ];
  };

  const getEqualsComparators = (
    value1 = 'eq',
    value2 = 'neq',
    label1 = t('is'),
    label2 = t('isNot')
  ) => {
    return [
      createComparator(value1, label1, IconEquals),
      createComparator(value2, label2, IconEqualsNot),
    ];
  };

  const getContainsComparators = (
    value1 = 'contains',
    value2 = 'ncontains',
    label1 = t('contains'),
    label2 = t('notContains')
  ) => {
    return [
      createComparator(value1, label1, IconContains),
      createComparator(value2, label2, IconContainsNot),
    ];
  };

  const getStartsEndsWithComparators = (
    value1 = 'starts_with',
    value2 = 'ends_with',
    label1 = t('startsWith'),
    label2 = t('endsWith')
  ) => {
    return [
      createComparator(value1, label1, IconStartsWith),
      createComparator(value2, label2, IconEndsWith),
    ];
  };

  const getComparator = (type: FieldTypeComparators = 'text'): SelectOption[] => {
    if (type === 'file') {
      return [
        ...getContainsComparators('file_contains', 'file_ncontains'),
        ...getStartsEndsWithComparators('file_starts_with', 'file_ends_with'),
        ...getEmptyComparators('file_empty', 'file_nempty'),
      ];
    } else if (type === 'date') {
      return [
        createComparator('date_lt', t('before'), IconDirectionUp),
        createComparator('date_lte', t('isOrBefore'), IconEqualsMore),
        ...getEqualsComparators('date_eq', 'date_neq'),
        createComparator('date_gte', t('isOrAfter'), IconEqualsLess),
        createComparator('date_gt', t('after'), IconDirectionDown),
        ...getEmptyComparators('date_empty', 'date_nempty'),
      ];
    } else if (type === 'select') {
      return [
        ...getEqualsComparators('select_eq', 'select_neq'),
        ...getContainsComparators('select_any_of', 'select_none_of', t('isAnyOf'), t('isNoneOf')),
        ...getEmptyComparators('select_empty', 'select_nempty'),
      ];
    } else if (type === 'boolean') {
      return getEqualsComparators('boolean_true', 'boolean_false', t('isTrue'), t('isFalse'));
    } else if (type === 'multi_select') {
      return [
        ...getEqualsComparators('select_multi_eq', 'select_multi_neq'),
        ...getContainsComparators(
          'select_multi_any_of',
          'select_multi_none_of',
          t('isAnyOf'),
          t('isNoneOf')
        ),
        ...getEmptyComparators('select_multi_empty', 'select_multi_nempty'),
      ];
    } else if (type === 'number') {
      return [
        createComparator('number_gt', t('moreThan'), IconGreaterThan),
        createComparator('number_gte', t('moreOrEqualTo'), IconGreaterThanOrEqual),
        ...getEqualsComparators('number_eq', 'number_neq', t('equalTo'), t('notEqualTo')),
        createComparator('number_lte', t('lessOrEqualTo'), IconLessThanOrEqual),
        createComparator('number_lt', t('lessThan'), IconLessThan),
        ...getStartsEndsWithComparators('number_starts_with', 'number_ends_with'),
        ...getEmptyComparators('number_empty', 'number_nempty'),
      ];
    }

    return [
      ...getEqualsComparators(),
      ...getContainsComparators(),
      ...getStartsEndsWithComparators(),
      ...getEmptyComparators(),
    ];
  };

  return { getComparator };
};

const locale = {
  en: {
    is: 'Is',
    isNot: 'Is not',
    isTrue: 'Is true',
    isFalse: 'Is false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    contains: 'Contains...',
    notContains: 'Does not contain...',
    isEmpty: 'Is empty',
    isNotEmpty: 'Is not empty',
    isAnyOf: 'Is any of...',
    isNoneOf: 'Is none of...',
    moreThan: 'More than',
    moreOrEqualTo: 'More or equal to',
    lessThan: 'Less than',
    lessOrEqualTo: 'Less or equal to',
    equalTo: 'Equal to',
    notEqualTo: 'Not equal to',
    startsWith: 'Starts with',
    endsWith: 'Ends with',
  },
};
